import React, { useRef } from "react"; // MODIFIED: Ensure useRef is imported
import { flexRender } from "@tanstack/react-table";
import AnalyticsParameterEditor from "../AnalyticsParameterEditor";
import StatusDropdown from "../Dropdowns/StatusDropdown";
import RatingDropdown from "../Dropdowns/RatingDropdown";
import { ChevronDownIcon } from "lucide-react";
import GenericDropdown from "../Dropdowns/GenericDropdown";

/**
 * TableCell Component
 *
 * A highly configurable table cell component that supports:
 * - Dynamic styling based on column conditions and values
 * - Inline editing with various input types (text, date, dropdowns)
 * - Column pinning with sticky positioning
 * - Analytics parameter editing
 * - Conditional background colors for visual indicators
 *
 * @param {Object} props - Component props
 * @param {Object} props.cell - TanStack table cell object containing column and value data
 * @param {Object} props.rowData - Complete row data object
 * @param {Object} props.activeColumns - Object mapping column keys to their active state
 * @param {Function} props.isColumnPinned - Function to check if a column is pinned
 * @param {Function} props.getPinnedColumnStyle - Function to get styling for pinned columns
 * @param {Object} props.columnConditions - Conditions for dynamic styling per column
 * @param {boolean} props.isAnalyticsTable - Whether this is an analytics table
 * @param {Object} props.editingCell - Current editing state {rowId, columnId}
 * @param {React.RefObject} props.editRef - Ref for the editing input element
 * @param {number} props.upperValue - Upper threshold for output column styling
 * @param {number} props.lowerValue - Lower threshold for output column styling
 * @param {Function} props.startEditingCell - Function to start editing a cell
 * @param {Function} props.onCommitEdit - Function to commit edit changes
 * @param {Function} props.setEditingCell - Function to set editing state
 * @param {*} props.tempValue - Temporary value during editing
 * @param {Function} props.setTempValue - Function to set temporary value
 * @param {Function} props.formatDateForInput - Function to format dates for input
 * @param {Array} props.companies - Array of company options for dropdown
 * @param {Array} props.teams - Array of team options for dropdown
 * @param {Array} props.departments - Array of department options for dropdown
 * @param {Array} props.positions - Array of position options for dropdown
 */
const TableCell = ({
  cell,
  rowData,
  activeColumns,
  isColumnPinned,
  getPinnedColumnStyle,
  columnConditions,
  isAnalyticsTable,
  editingCell,
  editRef, // Note: editRef's usage might need review for portal components
  upperValue,
  lowerValue,
  startEditingCell,
  onCommitEdit,
  setEditingCell,
  tempValue,
  setTempValue,
  formatDateForInput,
}) => {
  // ===== REFS =====
  const dropdownAnchorRef = useRef(null);

  // ===== BASIC CELL PROPERTIES =====
  const columnKey = cell.column.id;
  const value = cell.getValue();
  const isColumnActive = activeColumns[columnKey] || false;
  const isPinned = isColumnPinned(columnKey);
  const isDynamicColumn =
    columnKey.startsWith("P") || columnKey.startsWith("F");
  const isEditing =
    editingCell.rowId === rowData.id && editingCell.columnId === columnKey;
  const isDropdownColumn = [
    "status",
    "company",
    "team",
    "department",
    "position",
    "rating",
  ].includes(columnKey);

  // ===== STYLING PROPERTIES =====
  const isLeftAligned = columnKey === "status" || columnKey === "rating";
  const textAlignClass = isLeftAligned ? "text-left" : "text-center";
  const justifyClass = isLeftAligned ? "justify-start" : "justify-center";

  // Get column conditions for styling
  const conditions = columnConditions?.[columnKey] || null;

  // ===== CELL STYLING CALCULATIONS =====

  /**
   * Determines the appropriate styling class for the cell based on:
   * - Table type (analytics vs regular)
   * - Column type (output, dynamic, regular)
   * - Column conditions and values
   */
  const getCellStyling = () => {
    if (isAnalyticsTable) return "";

    if (columnKey === "output") {
      return getOutputColumnStyling();
    }

    if (isDynamicColumn && columnKey !== "output") {
      return getDynamicColumnStyling();
    }

    return "";
  };

  /**
   * Applies dynamic styling to P and F columns based on:
   * - Column conditions (upper/lower bounds)
   * - Color options (blue, yellow)
   * - Value range validation
   */
  const getDynamicColumnStyling = () => {
    if (!conditions) return "";

    const isEmptyOrDash =
      conditions.lower?.value === "" ||
      conditions.lower?.value === "-" ||
      conditions.upper?.value === "" ||
      conditions.upper?.value === "-";

    if (isEmptyOrDash) return "bg-white text-black";

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return "bg-white text-black";

    const isInGreenRange = checkIfInGreenRange(numericValue, conditions);
    const isBlue = conditions.colorOption === "blue";
    const isYellow = conditions.colorOption === "yellow";

    if (isBlue && isInGreenRange) return "bg-crutialBlue text-black";
    if (isYellow && !isInGreenRange) return "bg-crutialYellow text-black";
    return isInGreenRange
      ? "bg-tableBlue text-black"
      : "bg-figmaYellow text-black";
  };

  /**
   * Determines if a numeric value falls within the "green" (acceptable) range
   * based on upper and lower conditions with comparison operators
   *
   * @param {number} numericValue - The value to check
   * @param {Object} conditions - Conditions object with upper/lower bounds
   * @returns {boolean} Whether the value is in the acceptable range
   */
  const checkIfInGreenRange = (numericValue, conditions) => {
    const lowerValue = parseFloat(conditions.lower?.value);
    const upperValue = parseFloat(conditions.upper?.value);

    const lowerPass =
      isNaN(lowerValue) ||
      (conditions.lower?.condition === "<"
        ? numericValue >= lowerValue
        : numericValue <= lowerValue);

    const upperPass =
      isNaN(upperValue) ||
      (conditions.upper?.condition === ">"
        ? numericValue <= upperValue
        : numericValue >= upperValue);

    if (!isNaN(lowerValue) && !isNaN(upperValue)) return lowerPass && upperPass;
    if (!isNaN(lowerValue)) return lowerPass;
    if (!isNaN(upperValue)) return upperPass;

    return false;
  };

  /**
   * Applies styling to the output column based on the count of "red" cells
   * (cells that don't meet their conditions) in the row
   */
  const getOutputColumnStyling = () => {
    const redCount = countRedCells();

    if (redCount >= upperValue) return "bg-tableBlue text-black text-bold";
    if (redCount >= lowerValue) return "bg-figmaYellow text-black";
    return "bg-tableBlue text-black";
  };

  /**
   * Counts the number of P columns in the current row that don't meet
   * their defined conditions (considered "red" cells)
   */
  const countRedCells = () => {
    let redCount = 0;

    Object.keys(rowData).forEach((key) => {
      if (!key.startsWith("P") || !columnConditions?.[key]) return;

      const cellValue = parseFloat(rowData[key]);
      const condition = columnConditions[key];

      if (
        isNaN(cellValue) ||
        (!condition.lower?.value && !condition.upper?.value)
      )
        return;

      const isInRange = checkIfInGreenRange(cellValue, condition);
      if (!isInRange) redCount++;
    });

    return redCount;
  };

  // ===== EVENT HANDLERS =====

  /**
   * Handles keyboard events during editing
   * - Enter: Commit the edit
   * - Escape: Cancel the edit
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && onCommitEdit) {
      onCommitEdit();
    } else if (e.key === "Escape" && setEditingCell) {
      setEditingCell({ rowId: null, columnId: null });
    }
  };

  /**
   * Handles dropdown value changes and immediately commits the edit
   */
  const handleDropdownChangeAndCommit = (selected) => {
    console.log("Selected value:", selected);
    setTempValue(selected);
    if (onCommitEdit) {
      onCommitEdit(selected);
    }
  };

  /**
   * Handles click events - toggle for dropdowns, double click for others
   */
  const handleClick = () => {
    if (isDropdownColumn) {
      const isEditableColumn =
        isAnalyticsTable ||
        (!columnKey.startsWith("P") &&
          !columnKey.startsWith("F") &&
          columnKey !== "output");

      if (isEditableColumn) {
        // If already editing this cell, close the dropdown
        if (isEditing) {
          setEditingCell({ rowId: null, columnId: null });
        } else {
          // Start editing
          startEditingCell(rowData.id, columnKey, value);
        }
      }
    }
  };

  /**
   * Handles double-click events for non-dropdown columns
   */
  const handleDoubleClick = () => {
    if (!isDropdownColumn) {
      const isEditableColumn = isAnalyticsTable
        ? columnKey.startsWith("P") && !columnKey.startsWith("F") // Only P columns in analytics
        : !columnKey.startsWith("P") &&
          !columnKey.startsWith("F") &&
          columnKey !== "output";

      if (isEditableColumn) {
        startEditingCell(rowData.id, columnKey, value);
      }
    }
  };

  const handleDropdownClose = () => {
    setEditingCell({ rowId: null, columnId: null });
  };

  const dropdownConfigs = {
    status: {
      component: StatusDropdown,
      props: {
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        targetRef: dropdownAnchorRef,
      },
    },
    rating: {
      component: RatingDropdown,
      props: {
        selectedValue: tempValue ?? value,
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        targetRef: dropdownAnchorRef, // Assuming RatingDropdown is also portal-based
      },
    },
    company: {
      component: GenericDropdown,
      props: {
        selectedValue: tempValue ?? value,
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        dataType: "companies",
        placeholder: "Select Company",
      },
    },
    team: {
      component: GenericDropdown,
      props: {
        selectedValue: tempValue ?? value,
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        dataType: "teams",
        placeholder: "Select Team",
      },
    },
    department: {
      component: GenericDropdown,
      props: {
        selectedValue: tempValue ?? value,
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        dataType: "departments",
        placeholder: "Select Department",
      },
    },
    position: {
      component: GenericDropdown,
      props: {
        selectedValue: tempValue ?? value,
        onChange: handleDropdownChangeAndCommit,
        onClose: handleDropdownClose,
        dataType: "positions",
        placeholder: "Select Position",
      },
    },
  };

  // ===== RENDER EDITING COMPONENTS =====
  const renderEditingInput = () => {
    // Analytics parameter editor
    if (
      isAnalyticsTable &&
      columnKey.startsWith("P") &&
      columnKey.match(/^P\d+_\d+$/)
    ) {
      return (
        <AnalyticsParameterEditor
          rowData={rowData}
          columnId={columnKey}
          originalValue={value}
          onConfirm={() => onCommitEdit?.()}
          onCancel={() => setEditingCell({ rowId: null, columnId: null })}
        />
      );
    }

    if (dropdownConfigs[columnKey]) {
      const { component: Component, props } = dropdownConfigs[columnKey];
      return <Component {...props} />;
    }

    // Date input for date columns
    if (columnKey.includes("date_")) {
      return (
        <input
          type="date"
          autoFocus
          value={formatDateForInput(tempValue)}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full border rounded px-2 py-1 text-sm"
        />
      );
    }

    // Default text input
    return (
      <input
        type="text"
        autoFocus
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full border rounded px-2 py-1 text-sm"
        ref={editRef} // Example: if editRef is for focusing text inputs
      />
    );
  };

  // ===== FINAL STYLING =====
  let finalCellClass = getCellStyling();
  if (isPinned) {
    finalCellClass += " pinned-column pinned-column-shadow";
  }

  // ===== RENDER =====
  return (
    <td
      className={`whitespace-nowrap text-sm ${textAlignClass} ${finalCellClass}`}
      style={{
        width: isColumnActive ? "30px" : cell.column.getSize(),
        fontSize: isColumnActive ? "10px" : "inherit",
        overflow: isEditing ? "visible" : "hidden",
        textOverflow: "ellipsis",
        height: "25px",
        padding: "0",
        lineHeight: "0",
        position: isPinned ? "sticky" : "relative",
        backgroundColor: isPinned ? "white" : undefined,
        zIndex:
          isEditing &&
          !(
            dropdownConfigs[columnKey]?.component === StatusDropdown ||
            dropdownConfigs[columnKey]?.component === RatingDropdown
          )
            ? 50
            : isPinned
            ? 30
            : "auto", // Adjust zIndex for non-portal editing
        ...(isPinned ? getPinnedColumnStyle(columnKey) : {}),
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      data-column-index={columnKey}
    >
      <div // This is the outer wrapper div for cell content
        className={`${
          isPinned ? "pinned-content" : ""
        } flex font-gazer items-center ${justifyClass} h-full ${
          isLeftAligned ? "pl-2" : ""
        } relative`}
        style={{
          position: "relative",
          lineHeight: "normal",
        }}
        // ref={isEditing && !isDropdownColumn ? editRef : null} // editRef assignment might need to be more specific
      >
        {/* This div contains the actual cell content and acts as the anchor for dropdowns */}
        <div
          ref={dropdownAnchorRef}
          className={`flex items-center w-full px-1 ${
            isDropdownColumn && columnKey !== "rating"
              ? "justify-between"
              : justifyClass
          }`}
        >
          <div className="flex-1">
            <span>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </span>
          </div>
          {isDropdownColumn && columnKey !== "rating" && (
            <div
              className={`${isEditing ? "bg-blue-500 rounded" : ""}`}
              style={{
                backgroundColor: isEditing ? "#0832DE" : "transparent",
                borderRadius: isEditing ? "4px" : "0",
                padding: isEditing ? "2px" : "0",
              }}
            >
              <ChevronDownIcon
                className="w-4 h-4 cursor-pointer"
                color={isEditing ? "#FFFFFF" : "#B9BDC4"}
              />
            </div>
          )}
        </div>

        {isEditing &&
          (() => {
            const editorComponentInstance = renderEditingInput();
            if (!editorComponentInstance) return null;

            const currentEditorConfig = dropdownConfigs[columnKey]; // Now correctly accessed

            if (
              currentEditorConfig?.component === StatusDropdown ||
              currentEditorConfig?.component === RatingDropdown // Assuming RatingDropdown is also portal-based
            ) {
              return editorComponentInstance;
            }

            if (
              isAnalyticsTable &&
              columnKey.startsWith("P") &&
              columnKey.match(/^P\d+_\d+$/)
            ) {
              return editorComponentInstance;
            }

            // Other inline editors (GenericDropdown, text, date inputs) are wrapped
            // to be positioned absolutely within the cell.
            return (
              <div className="absolute inset-0" style={{ zIndex: 1000 }}>
                {" "}
                {/* Higher zIndex for inline editors */}
                {editorComponentInstance}
              </div>
            );
          })()}
      </div>
    </td>
  );
};

export default TableCell;
